import { normalizeAddressType } from '#/modules/crmMgmt/customer/helper/customerAddress';

/**
 * 客戶 create／update 寫入 payload。
 * - addresses：merge-only（帶 id＝改、無 id＝增；不可刪）；**不送** lineNo（response 唯讀）。
 * - contacts：replace-all；可送 lineNo（10、20…）；省略時後端依陣列序產生。
 * 預設地址：帳單／出貨用途各最多一個；BOTH 同時佔兩個用途。
 * 禁止送 status。
 */
export function buildCustomerWritePayload(values, { updatedAt = null } = {}) {
  const payload = {
    code: values?.code,
    name: values?.name,
    chineseName: emptyToNull(values?.chineseName),
    englishName: emptyToNull(values?.englishName),
    taxIdNumber: emptyToNull(values?.taxIdNumber),
    invoiceTitle: emptyToNull(values?.invoiceTitle),
    snCode2: emptyToNullUpper(values?.snCode2),
    snCode3: emptyToNullUpper(values?.snCode3),
    taxCountryCode: emptyToNullUpper(values?.taxCountryCode),
    homepage: emptyToNull(values?.homepage),
    phone: emptyToNull(values?.phone),
    fax: emptyToNull(values?.fax),
    remark: emptyToNull(values?.remark),
    addresses: mapAddressRowsForWrite(values?.addresses),
    contacts: mapContactRowsWithLineNo(values?.contacts),
  };

  if (updatedAt != null && updatedAt !== '') {
    payload.updatedAt = updatedAt;
  }

  return payload;
}

export function mapCustomerDetailToForm(detail) {
  return {
    code: detail?.code ?? '',
    name: detail?.name ?? '',
    chineseName: detail?.chineseName ?? '',
    englishName: detail?.englishName ?? '',
    taxIdNumber: detail?.taxIdNumber ?? '',
    invoiceTitle: detail?.invoiceTitle ?? '',
    snCode2: detail?.snCode2 ?? '',
    snCode3: detail?.snCode3 ?? '',
    taxCountryCode: detail?.taxCountryCode ?? '',
    homepage: detail?.homepage ?? '',
    phone: detail?.phone ?? '',
    fax: detail?.fax ?? '',
    remark: detail?.remark ?? '',
    addresses: (detail?.addresses ?? []).map(mapAddressToForm),
    contacts: (detail?.contacts ?? []).map(mapContactToForm),
  };
}

export function createEmptyAddressRow() {
  return {
    addressType: '',
    postalCode: '',
    address: '',
    isDefault: false,
    remark: '',
  };
}

export function createEmptyContactRow() {
  return {
    name: '',
    department: '',
    title: '',
    phone: '',
    mobile: '',
    email: '',
    isDefault: false,
    remark: '',
  };
}

function emptyToNull(value) {
  if (value == null) {
    return null;
  }
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
}

function emptyToNullUpper(value) {
  const trimmed = emptyToNull(value);
  return trimmed == null ? null : trimmed.toUpperCase();
}

function withRowId(mapped, row) {
  if (row?.id) {
    mapped.id = row.id;
  }
  return mapped;
}

function mapAddressRowsForWrite(rows) {
  let billingDefaultSeen = false;
  let shippingDefaultSeen = false;

  return (rows ?? []).map((row) => {
    const type = normalizeAddressType(row?.addressType);
    const wantsDefault = Boolean(row?.isDefault);
    const coversBilling = type === 'BILLING' || type === 'BOTH';
    const coversShipping = type === 'SHIPPING' || type === 'BOTH';
    const billingOk = !coversBilling || !billingDefaultSeen;
    const shippingOk = !coversShipping || !shippingDefaultSeen;
    const isDefault = wantsDefault && billingOk && shippingOk;

    if (isDefault) {
      if (coversBilling) {
        billingDefaultSeen = true;
      }
      if (coversShipping) {
        shippingDefaultSeen = true;
      }
    }

    return mapAddressForWrite(row, isDefault);
  });
}

function mapContactRowsWithLineNo(rows) {
  let defaultSeen = false;
  return (rows ?? []).map((row, index) => {
    const wantsDefault = Boolean(row?.isDefault);
    const isDefault = wantsDefault && !defaultSeen;
    if (isDefault) {
      defaultSeen = true;
    }
    return mapContactForWrite(row, (index + 1) * 10, isDefault);
  });
}

/** Address request 不接受 line_no（後端 response 唯讀排序）。 */
function mapAddressForWrite(row, isDefault) {
  return withRowId({
    addressType: normalizeAddressType(row?.addressType) || null,
    postalCode: emptyToNull(row?.postalCode),
    address: String(row?.address ?? '').trim(),
    isDefault,
    remark: emptyToNull(row?.remark),
  }, row);
}

function mapContactForWrite(row, lineNo, isDefault) {
  return withRowId({
    lineNo,
    name: String(row?.name ?? '').trim(),
    department: emptyToNull(row?.department),
    title: emptyToNull(row?.title),
    phone: emptyToNull(row?.phone),
    mobile: emptyToNull(row?.mobile),
    email: emptyToNull(row?.email),
    isDefault,
    remark: emptyToNull(row?.remark),
  }, row);
}

function mapAddressToForm(row) {
  return {
    id: row?.id,
    addressType: normalizeAddressType(row?.addressType),
    postalCode: row?.postalCode ?? '',
    address: row?.address ?? '',
    isDefault: Boolean(row?.isDefault),
    remark: row?.remark ?? '',
  };
}

function mapContactToForm(row) {
  return {
    id: row?.id,
    name: row?.name ?? '',
    department: row?.department ?? '',
    title: row?.title ?? '',
    phone: row?.phone ?? '',
    mobile: row?.mobile ?? '',
    email: row?.email ?? '',
    isDefault: Boolean(row?.isDefault),
    remark: row?.remark ?? '',
  };
}
